"use client";

import type React from "react";
import { createContext, useState, useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { supportedLanguages } from "../constants/common";

type Language = "en" | "fr";

type LanguageContextType = {
  language: Language;
  changeLanguage: (lang: Language) => void;
};

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [language, setLanguage] = useState<Language>("en");
  const [isInitialized, setIsInitialized] = useState(false);
  const isProgrammaticRef = useRef(false);

  useEffect(() => {
    const pathLang = location.pathname.split("/")[1];
    let initialLang: Language = "en";
    if (supportedLanguages.includes(pathLang)) {
      initialLang = pathLang as Language;
    } else {
      const savedLang = localStorage.getItem("lang") as Language | null;
      initialLang =
        savedLang && supportedLanguages.includes(savedLang) ? savedLang : "en";
    }

    setLanguage(initialLang);
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem("lang", language);
      i18n.changeLanguage(language);

      // Update URL with language prefix
      const currentPath = location.pathname;
      const pathArray = currentPath.split("/");
      const currentLangInPath = pathArray[1];

      if (currentLangInPath !== language) {
        // Replace the language prefix in the URL
        const newPath = currentPath.replace(/^\/[^/]*/, `/${language}`);
        isProgrammaticRef.current = true;
        navigate(newPath, { replace: true });
        // Reset the ref after navigation
        setTimeout(() => {
          isProgrammaticRef.current = false;
        }, 0);
      }
    }
  }, [language, i18n, navigate, location.pathname, isInitialized]);

  useEffect(() => {
    const pathLang = location.pathname.split("/")[1];
    if (
      isInitialized &&
      !isProgrammaticRef.current &&
      supportedLanguages.includes(pathLang) &&
      pathLang !== language
    ) {
      setLanguage(pathLang as Language);
    }
  }, [location.pathname, isInitialized, language]);

  const changeLanguage = (lang: Language) => {
    if (supportedLanguages.includes(lang)) {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
